/**
 * `/sessions/:id/close`: "Clôture" — confirm what closing the session will
 * hand back to the stock before actually doing it.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { closeSessionRequest, getSession } from '../../../api/sessions';
import { Button, Skeleton, useToast } from '../../../components/ui';
import { t } from '../../../i18n/fr';
import { sessionErrorMessage } from '../errors';

import type { SessionWithExtras } from '../../../api/types';

/**
 * Render the "Clore la session" confirmation screen.
 *
 * @returns The page element.
 */
export function ClosePage() {
  const { id } = useParams<{ id: string }>();
  const sessionId = Number(id);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [session, setSession] = useState<SessionWithExtras | null>(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getSession(sessionId)
      .then((data) => {
        if (!cancelled) {
          setSession(data);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          showToast(sessionErrorMessage(error), 'error');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, showToast]);

  async function handleClose() {
    setClosing(true);
    try {
      await closeSessionRequest(sessionId);
      navigate(`/sessions/${sessionId}/summary`);
    } catch (error) {
      showToast(sessionErrorMessage(error), 'error');
      setClosing(false);
    }
  }

  if (loading) {
    return (
      <div className="pile" aria-hidden="true">
        <Skeleton height="3rem" />
        <Skeleton height="10rem" />
      </div>
    );
  }

  if (!session) {
    return null;
  }

  const holders = session.participants.filter((participant) => participant.current_door);

  return (
    <div className="bloc">
      <h2>{t('session.close.title', { name: session.name })}</h2>
      <p className="muted" style={{ margin: '0 0 var(--space-3)' }}>
        {t('session.close.lead', { count: session.participants.length })}
      </p>

      {holders.length > 0 ? (
        <div className="rendu">
          <p style={{ margin: '0 0 var(--space-2)' }}>
            <b>{t('session.close.pendingWarningTitle', { count: holders.length })}</b>
          </p>
          <p style={{ margin: 0 }}>{t('session.close.pendingWarningBody')}</p>
        </div>
      ) : null}

      {holders.length > 0 ? (
        <div className="parts" style={{ marginTop: 'var(--space-3)' }}>
          {holders.map((participant) => (
            <div className="part" key={participant.id}>
              <b>{participant.user.first_name}</b>
              <span>{participant.current_door}</span>
              <span className="n">1</span>
            </div>
          ))}
        </div>
      ) : null}

      <div className="pile" style={{ marginTop: 'var(--space-4)' }}>
        <Button variant="primary" fullWidth disabled={closing} onClick={() => void handleClose()}>
          {t('session.close.confirm')}
        </Button>
        <Button variant="ghost" fullWidth onClick={() => navigate(`/sessions/${sessionId}`)}>
          {t('session.close.continue')}
        </Button>
      </div>
    </div>
  );
}
